import { FuneralServicesFilters, FuneralServiceSummary } from 'embed/js/types';
import { Dispatch, useCallback, useEffect, useRef, useState } from 'react';
import { useLocalStorage, useUpdateEffect } from 'usehooks-ts';
import Api from '../api';
import { Branch, City, Organization, Region } from '../types';
import AlternatingStyle from './ListStyles/AlternatingStyle';
import CardStyle from './ListStyles/CardStyle';
import DefaultStyle from './ListStyles/DefaultStyle';
import MobileStyle from './ListStyles/MobileStyle';
import TiledStyle from './ListStyles/TiledStyle';
import { FilterFields } from './Partials/FilterFields';
import { SummaryList } from './Partials/SummaryList';

const useIntersectionObserver = (options?: IntersectionObserverInit): [Dispatch<HTMLElement>, IntersectionObserverEntry | null] => {
  const [entry, setEntry] = useState<IntersectionObserverEntry | null>(null);
  const [node, setNode] = useState<HTMLElement | null>(null);

  const observer = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(([entry]) => setEntry(entry), options);

    const { current: currentObserver } = observer;

    if (node) currentObserver.observe(node);

    return () => currentObserver.disconnect();
  }, [node, JSON.stringify(options)]);

  return [setNode, entry];
};

const List = ({ listCode, listStyle: listStyleOverride, search }: { listCode: string; listStyle: string | null; search: string }) => {
  const [filters, setFilters] = useState<FuneralServicesFilters>({
    search: search,
    regions: [],
    cities: [],
    branch: [],
  });

  const [page, setPage] = useState(1);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [filterOptions, setFilterOptions] = useState<{
    regions: Region[];
    cities: City[];
    branches: Branch[];
  } | null>(null);

  const [isMobile, setIsMobile] = useState(window.innerHeight > window.innerWidth || window.innerWidth < 768);
  useEffect(() => {
    (async function () {
      if (!filterOptions && !organization) {
        Api.filterDataAndOrg(listCode, { countries: [1] }).then(([branches, regions, organization]) => {
          const newFilterOptions = {
            regions: regions,
            branches: branches,
            cities: regions.length ? (regions.find((r) => r.id === filters.regions[0]) as Region)?.cities : [],
          };
          setFilterOptions(newFilterOptions);
          setOrganization(organization);
        });
      }

      refreshData();

      window.addEventListener('resize', () => setIsMobile(window.innerHeight > window.innerWidth));
    })();

    return window.removeEventListener('resize', () => setIsMobile(window.innerHeight > window.innerWidth));
  }, []);

  const [services, setServices] = useLocalStorage<FuneralServiceSummary[]>('services-cache', []);

  const [setRef, intersection] = useIntersectionObserver();
  const refreshData = useCallback(() => {
    Api.services({
      hash: listCode,
      page: 1,
      ...filters,
    }).then((r) => {
      setServices(r.data);
      setPage(1);
    });
  }, [listCode, filters]);

  useUpdateEffect(() => {
    refreshData();
  }, [filters]);

  useUpdateEffect(() => {
    if (!intersection?.isIntersecting) return;
    Api.services({
      hash: listCode,
      page: page + 1,
      ...filters,
    }).then((r) => {
      if (r.current_page === page) return;
      setServices((services) => [...services, ...r.data]);
      setPage(page + 1);
    });
  }, [intersection?.isIntersecting]);

  if (!organization) return null;

  const listProps = {
    scrollElementRef: setRef,
    services: services,
    organization: organization,
  };

  let ListStyle = DefaultStyle;

  if (listStyleOverride) {
    ListStyle =
      {
        mobile: MobileStyle,
        alternating: AlternatingStyle,
        tiled: TiledStyle,
        card: CardStyle,
      }[listStyleOverride] || DefaultStyle;
  } else if (isMobile && organization.embed_list_style !== 'card') {
    ListStyle = MobileStyle;
  } else if (organization.embed_list_style === 'alternating') {
    ListStyle = AlternatingStyle;
  } else if (organization.embed_list_style === 'tiled') {
    ListStyle = TiledStyle;
  } else if (organization.embed_list_style === 'card') {
    ListStyle = CardStyle;
  }

  const isAssociation = organization?.type === 'association';
  const isUniversal = organization?.type === 'universal';
  if (!!document.getElementById('fs-loader-container')) {
    // @ts-ignore TS is being stupid
    document.getElementById('fs-loader-container').style.display = 'none';
  }

  return (
    <div className="main-embed">
      <FilterFields filterOptions={filterOptions} setFilters={setFilters} showAdditionalFields={isAssociation || isUniversal} filters={filters} />

      <SummaryList organization={organization} filters={filters} />

      <ListStyle {...listProps} />
    </div>
  );
};

export default List;
